if (!customElements.get('build-a-bundle')) {
  customElements.define(
    'build-a-bundle',
    class BuildABundle extends HTMLElement {
      constructor() {
        super();

        this.currency = this.dataset.currency || 'USD';
        this.discountType = this.dataset.discountType;
        this.flatPercent = parseFloat(this.dataset.flatPercent) || 0;
        this.flatAmountCents = parseInt(this.dataset.flatAmount, 10) || 0;
        this.tiers = [
          { min: parseInt(this.dataset.tier1Min, 10) || 0, percent: parseFloat(this.dataset.tier1Percent) || 0 },
          { min: parseInt(this.dataset.tier2Min, 10) || 0, percent: parseFloat(this.dataset.tier2Percent) || 0 },
          { min: parseInt(this.dataset.tier3Min, 10) || 0, percent: parseFloat(this.dataset.tier3Percent) || 0 },
        ].filter((tier) => tier.min > 0);
        this.gateMinimum = this.dataset.gateMinimum === 'true';
        this.minimumItems = parseInt(this.dataset.minimumItems, 10) || 0;

        this.inputs = Array.from(this.querySelectorAll('.build-a-bundle__item-input'));
        this.countEl = this.querySelector('[data-selected-count]');
        this.messageEl = this.querySelector('[data-summary-message]');
        this.comparePriceEl = this.querySelector('[data-compare-price]');
        this.currentPriceEl = this.querySelector('[data-current-price]');
        this.errorWrapper = this.querySelector('.build-a-bundle__error-message-wrapper');
        this.errorMessage = this.querySelector('.build-a-bundle__error-message');
        this.addButton = this.querySelector('.build-a-bundle__add-button');
        this.buyNowButton = this.querySelector('.build-a-bundle__buy-now-button');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.inputs.forEach((input) => input.addEventListener('change', this.onQuantityChange.bind(this)));
        this.querySelectorAll('[data-variant-select]').forEach((select) => {
          select.addEventListener('change', this.onVariantChange.bind(this));
        });
        if (this.addButton) this.addButton.addEventListener('click', () => this.onAddToCart(false));
        if (this.buyNowButton) this.buyNowButton.addEventListener('click', () => this.onAddToCart(true));

        this.updateSummary();
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      }

      get selectedItems() {
        return this.inputs
          .map((input) => ({
            id: input.dataset.variantId,
            quantity: parseInt(input.value, 10) || 0,
            priceCents: Math.round(parseFloat(input.dataset.price)),
            title: input.dataset.title,
          }))
          .filter((item) => item.quantity > 0);
      }

      activeTierFor(count) {
        let active = null;
        this.tiers.forEach((tier) => {
          if (count >= tier.min && (!active || tier.min > active.min)) active = tier;
        });
        return active;
      }

      onQuantityChange() {
        this.updateSummary();
      }

      onVariantChange(event) {
        const select = event.target;
        const card = select.closest('.build-a-bundle__card');
        if (!card) return;

        const option = select.options[select.selectedIndex];
        const input = card.querySelector('.build-a-bundle__item-input');
        const priceEl = card.querySelector('[data-card-price]');

        if (input) {
          input.dataset.variantId = select.value;
          input.dataset.price = option.dataset.price;
        }
        if (priceEl) priceEl.textContent = option.dataset.priceFormatted;

        this.updateSummary();
      }

      updateSummary() {
        const items = this.selectedItems;
        const count = items.reduce((sum, item) => sum + item.quantity, 0);
        const regularCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);

        if (this.countEl) this.countEl.textContent = count;

        let discountedCents = regularCents;
        let discountLabel = '';

        if (this.discountType === 'flat_percent' && count > 0) {
          discountedCents = Math.round(regularCents * (1 - this.flatPercent / 100));
          discountLabel = `${this.flatPercent}% off`;
        } else if (this.discountType === 'flat_amount' && count > 0) {
          discountedCents = Math.max(0, regularCents - this.flatAmountCents);
          discountLabel = `${this.formatMoney(this.flatAmountCents)} off`;
        } else if (this.discountType === 'tiered') {
          const tier = this.activeTierFor(count);
          if (tier) {
            discountedCents = Math.round(regularCents * (1 - tier.percent / 100));
            discountLabel = `${tier.percent}% off`;
          }
        }

        if (this.currentPriceEl) this.currentPriceEl.textContent = this.formatMoney(discountedCents);
        if (this.comparePriceEl) {
          if (discountedCents < regularCents) {
            this.comparePriceEl.textContent = this.formatMoney(regularCents);
            this.comparePriceEl.hidden = false;
          } else {
            this.comparePriceEl.hidden = true;
          }
        }

        if (this.messageEl) {
          if (this.discountType === 'tiered' && this.tiers.length > 0) {
            const nextTier = this.tiers
              .filter((tier) => tier.min > count)
              .sort((a, b) => a.min - b.min)[0];
            if (discountLabel && nextTier) {
              this.messageEl.textContent = `${discountLabel} unlocked — add ${nextTier.min - count} more for ${nextTier.percent}% off`;
            } else if (discountLabel) {
              this.messageEl.textContent = `${discountLabel} unlocked`;
            } else if (nextTier) {
              this.messageEl.textContent = `Add ${nextTier.min - count} more item${nextTier.min - count === 1 ? '' : 's'} to unlock ${nextTier.percent}% off`;
            } else {
              this.messageEl.textContent = '';
            }
          } else if (discountLabel) {
            this.messageEl.textContent = `${discountLabel} unlocked`;
          } else {
            this.messageEl.textContent = '';
          }
        }

        const meetsMinimum = !this.gateMinimum || count >= this.minimumItems;
        const canCheckout = count > 0 && meetsMinimum;

        [this.addButton, this.buyNowButton].forEach((button) => {
          if (!button) return;
          button.toggleAttribute('aria-disabled', !canCheckout);
        });

        if (this.gateMinimum && count > 0 && !meetsMinimum) {
          this.setError(
            `Select at least ${this.minimumItems} items to add this bundle to your cart (${this.minimumItems - count} more needed).`
          );
        } else {
          this.setError(false);
        }
      }

      onAddToCart(redirectToCheckout) {
        this.setError(false);

        const items = this.selectedItems;
        if (items.length === 0) return;

        const count = items.reduce((sum, item) => sum + item.quantity, 0);
        if (this.gateMinimum && count < this.minimumItems) {
          this.setError(`Select at least ${this.minimumItems} items to add this bundle to your cart.`);
          return;
        }

        const activeButton = redirectToCheckout ? this.buyNowButton : this.addButton;
        [this.addButton, this.buyNowButton].forEach((button) => {
          if (!button) return;
          button.setAttribute('aria-disabled', 'true');
        });
        if (activeButton) {
          activeButton.classList.add('loading');
          const spinner = activeButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        const config = fetchConfig('json');
        config.body = JSON.stringify({
          items: items.map(({ id, quantity }) => ({
            id,
            quantity,
            properties: { Bundle: this.dataset.bundleName },
          })),
          sections: !redirectToCheckout && this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
          sections_url: window.location.pathname,
        });

        if (!redirectToCheckout && this.cart) this.cart.setActiveElement(document.activeElement);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message);
              return;
            }

            if (redirectToCheckout) {
              window.location = window.routes.cart_url + '/checkout';
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'build-a-bundle',
              cartData: response,
            });

            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            if (!response.key && response.items && response.items.length > 0) {
              response.key = response.items[response.items.length - 1].key;
            }

            this.cart.renderContents(response);

            this.inputs.forEach((input) => {
              input.value = 0;
            });
            this.updateSummary();
          })
          .catch((e) => {
            console.error(e);
            this.setError('Something went wrong adding these items to your cart. Please try again.');
          })
          .finally(() => {
            [this.addButton, this.buyNowButton].forEach((button) => {
              if (!button) return;
              button.classList.remove('loading');
              const spinner = button.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            });
            this.updateSummary();
          });
      }

      setError(message) {
        if (!this.errorWrapper) return;
        this.errorWrapper.toggleAttribute('hidden', !message);
        this.errorMessage.textContent = message || '';
      }
    }
  );
}